import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, AlertCircle, Loader2 } from "lucide-react";
import { useResendVerificationEmail, useVerifyEmail } from "../hooks/control";

type VerifyState =
	| "loading"
	| "success"
	| "already_verified"
	| "expired"
	| "invalid"
	| "error";

export function VerifyEmail() {
	const [searchParams] = useSearchParams();
	const [state, setState] = useState<VerifyState>("loading");
	const [message, setMessage] = useState("");
	const [_email, setEmail] = useState("");
	const [resendLoading, setResendLoading] = useState(false);
	const [resendDone, setResendDone] = useState(false);
	const { verifyEmail } = useVerifyEmail();
	const { resendVerificationEmail } = useResendVerificationEmail();

	const token = searchParams.get("token");
	const emailParam = searchParams.get("email");

	useEffect(() => {
		if (!token || !emailParam) {
			setState("invalid");
			setMessage("Enlace de verificación inválido o incompleto.");
			return;
		}

		let isMounted = true;

		// Espera al primer render estable para evitar dobles llamadas en montajes rápidos.
		const timeoutId = window.setTimeout(async () => {
			try {
				const data = await verifyEmail(token, emailParam);

				if (!isMounted || !data) return;

				if (data.success) {
					if (data.alreadyVerified) {
						setState("already_verified");
					} else {
						setState("success");
					}
					setMessage(data.message ?? "");
					if (data.email) setEmail(data.email);
				} else if (data.expired) {
					setState("expired");
					setMessage(data.message ?? "");
					if (data.email) setEmail(data.email);
				} else {
					setState("invalid");
					setMessage(data.message ?? "Token de verificación inválido.");
				}
			} catch (error) {
				if (!isMounted) return;
				setState("error");
				setMessage(
					error instanceof Error
						? error.message
						: "No se pudo conectar con el servidor. Intenta más tarde.",
				);
			}
		}, 250);

		return () => {
			isMounted = false;
			window.clearTimeout(timeoutId);
		};
	}, [token, emailParam, verifyEmail]);

	const handleResend = async () => {
		if (!emailParam) return;
		setResendLoading(true);
		try {
			const payload = await resendVerificationEmail(emailParam);
			if (payload?.success) {
				setResendDone(true);
			} else {
				setMessage(payload?.message || "No se pudo reenviar el correo.");
			}
		} catch {
			// silently fail
		} finally {
			setResendLoading(false);
		}
	};

	return (
		<div className='flex min-h-screen items-center justify-center bg-linear-to-br from-primary/10 to-slate-100 px-4'>
			<div className='w-full max-w-md rounded-2xl bg-white px-10 py-10 text-center shadow-[0_16px_40px_rgba(15,23,42,0.12)]'>
				{state === "loading" && (
					<>
						<Loader2 className='mx-auto h-14 w-14 animate-spin text-primary' />
						<h2 className='mt-5 text-xl font-medium text-slate-700'>
							Verificando tu correo...
						</h2>
						<p className='mt-2 text-sm text-slate-500'>Espera un momento.</p>
					</>
				)}

				{state === "success" && (
					<>
						<CheckCircle className='mx-auto h-14 w-14 text-emerald-500' />
						<h2 className='mt-5 text-xl font-medium text-slate-700'>
							¡Correo verificado!
						</h2>
						<p className='mt-2 text-sm text-slate-500'>
							{message ||
								"Tu cuenta ha sido verificada exitosamente. Ya puedes iniciar sesión."}
						</p>
						<Link
							to='/login'
							className='mt-7 inline-block w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-white transition hover:bg-primary-hover'
						>
							Iniciar sesión
						</Link>
					</>
				)}

				{state === "already_verified" && (
					<>
						<CheckCircle className='mx-auto h-14 w-14 text-sky-500' />
						<h2 className='mt-5 text-xl font-medium text-slate-700'>
							Cuenta ya verificada
						</h2>
						<p className='mt-2 text-sm text-slate-500'>
							{message || "Esta cuenta ya fue verificada anteriormente."}
						</p>
						<Link
							to='/login'
							className='mt-7 inline-block w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-white transition hover:bg-primary-hover'
						>
							Iniciar sesión
						</Link>
					</>
				)}

				{state === "expired" && (
					<>
						<AlertCircle className='mx-auto h-14 w-14 text-amber-500' />
						<h2 className='mt-5 text-xl font-medium text-slate-700'>
							Enlace expirado
						</h2>
						<p className='mt-2 text-sm text-slate-500'>
							{message || "El enlace de verificación ha expirado."}
						</p>
						{!resendDone ? (
							<button
								type='button'
								onClick={handleResend}
								disabled={resendLoading}
								className='mt-7 w-full cursor-pointer rounded-full bg-primary py-2.5 text-sm font-semibold text-white transition hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60'
							>
								{resendLoading
									? "Enviando..."
									: "Reenviar correo de verificación"}
							</button>
						) : (
							<p className='mt-5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700'>
								Correo reenviado. Revisa tu bandeja de entrada.
							</p>
						)}
					</>
				)}

				{(state === "invalid" || state === "error") && (
					<>
						<XCircle className='mx-auto h-14 w-14 text-rose-500' />
						<h2 className='mt-5 text-xl font-medium text-slate-700'>
							Error de verificación
						</h2>
						<p className='mt-2 text-sm text-slate-500'>
							{message ||
								"El enlace no es válido. Por favor solicita uno nuevo."}
						</p>
						
						{emailParam && !resendDone ? (
							<button
								type='button'
								onClick={handleResend}
								disabled={resendLoading}
								className='mt-7 w-full cursor-pointer rounded-full bg-primary py-2.5 text-sm font-semibold text-white transition hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60'
							>
								{resendLoading
									? "Enviando..."
									: "Reenviar correo de verificación"}
							</button>
						) : emailParam && resendDone ? (
							<p className='mt-5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700'>
								Correo reenviado. Revisa tu bandeja de entrada.
							</p>
						) : null}
						
						<Link
							to='/'
							className={`mt-4 inline-block w-full rounded-full ${emailParam ? 'border border-slate-200 text-slate-600 hover:bg-slate-50' : 'bg-primary text-white hover:bg-primary-hover'} py-2.5 text-sm font-semibold transition`}
						>
							Volver al inicio
						</Link>
					</>
				)}
			</div>
		</div>
	);
}
